import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

const namesByLocale = new Map<string, Map<string, string>>();

/** Codepoints of `emoji` as the dash separated hex form used by the data set. */
function toUnified(emoji: string): string {
  return [...emoji]
    .map(character => character.codePointAt(0)?.toString(16) ?? '')
    .join('-');
}

function namesFor(locale: string, emojiData: EmojiData): Map<string, string> {
  const cached = namesByLocale.get(locale);
  if (cached) return cached;

  const names = new Map<string, string>();
  for (const entries of Object.values(emojiData.emojis)) {
    for (const entry of entries) {
      // the data set lists alternative names too, the first one is the
      // canonical, readable one
      const name = entry.n[entry.n.length - 1];
      if (!name) continue;
      names.set(entry.u, name);
      // skin tone variations carry the same name
      entry.v?.forEach(variation => names.set(variation, name));
    }
  }

  namesByLocale.set(locale, names);
  return names;
}

/**
 * Localized name of a single emoji grapheme, e.g. "grinning face", or
 * `undefined` when the data set does not know it.
 *
 * The variation selector U+FE0F is optional in the data, so both spellings are
 * probed before giving up.
 */
export function getEmojiName(
  emoji: string,
  emojiData: EmojiData,
  locale: string = window.SenseConstants?.locale ?? 'en'
): string | undefined {
  const names = namesFor(locale, emojiData);
  const unified = toUnified(emoji);
  return (
    names.get(unified) ??
    names.get(unified.replace(/-fe0f/g, '')) ??
    names.get(`${unified}-fe0f`)
  );
}
